import { useRouter } from 'next/router';
import { useCallback } from 'react';

import useAccessBadge from './useAccessBadge';
import { useMetrics } from './useMetrics';
import { PaywallType } from './usePaywallEmailTracking';
import useTeachingCreditState from './useTeachingCreditState';

export type PaywallCta = 'learn-more' | 'fill-out-form' | 'see-pricing' | 'chat-with-us';

export interface PaywallCtaContext {
  paywallType: PaywallType;
  /** Semantic source label, e.g. 'designer-start'. */
  source?: string;
  /** Legacy `usePaywallPrompt` screen label, e.g. 'schoolPlanPaywall'. */
  screen?: string;
  /** SchoolPlanTrialModal / WeeklySessionLimitModal state, e.g. 'hard-block' | 'home'. */
  modalState?: string;
}

const EVENT_NAME_BY_CTA: Record<PaywallCta, string> = {
  'learn-more': 'Paywall.LearnMoreClicked',
  'fill-out-form': 'Paywall.FillOutFormClicked',
  'see-pricing': 'Paywall.SeePricingClicked',
  'chat-with-us': 'Paywall.ChatWithUsClicked',
};

/**
 * Single source of truth for tracking paywall CTA clicks across the three
 * paywall modals (WeeklySessionLimit, SchoolPlanTrial, PremiumFeatureGate).
 * Mirrors the enrichment shape of `usePaywallEmailTracking` so funnel queries
 * can join CTA clicks with email submissions on `source`, `paywallType`, and
 * `modalState`.
 */
export function usePaywallCtaTracking() {
  const router = useRouter();
  const metrics = useMetrics();
  const { accessBadge } = useAccessBadge();
  const { creditState } = useTeachingCreditState();

  return useCallback(
    (cta: PaywallCta, ctx: PaywallCtaContext) => {
      const props = {
        cta,
        page: router.pathname,
        paywallType: ctx.paywallType,
        source: ctx.source,
        screen: ctx.screen,
        modalState: ctx.modalState,
        organizationId: accessBadge?.organization?.id,
        plan: accessBadge?.license?.coarseLevel,
        subPlan: accessBadge?.license?.fineLevel,
        used: creditState?.used,
        remaining: creditState?.remaining,
        weeklyRate: creditState?.weeklyRate,
        bonusRemaining: creditState?.bonusRemaining,
        limit: creditState?.limit,
        hasReachedLimit: creditState?.hasReachedLimit,
        premiumTestPrepPlayed: creditState?.premiumTestPrepPlayed,
        premiumTestPrepRemaining: creditState?.premiumTestPrepRemaining,
        premiumTestPrepLimit: creditState?.premiumTestPrepLimit,
        premiumTestPrepHasReachedLimit: creditState?.premiumTestPrepHasReachedLimit,
      };
      metrics.logEvent(EVENT_NAME_BY_CTA[cta], props);
    },
    [router, metrics, accessBadge, creditState],
  );
}

export default usePaywallCtaTracking;
